import React, { useState, useEffect, useMemo } from 'react';

import {
  Button,
  CheckboxGroup,
  RadioGroup,
  ProgressBar,
} from '../../../components/_shared';
import { evaluateAnswers } from '../../../service/user';
import { QuizContainer } from './styles';
import arrLeft from '../../../assets/imgs/arrow-left.png';
import arrRight from '../../../assets/imgs/arrow-right.png';
import { replaceVideoScripts } from '../../../utils/helper';
import {
  ClockIcon,
  QuestionIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from '../../../assets/icons';
import '../description.scss';
import './quiz.scss';
import { notify } from '../../../utils/helper';

const Quizzes = ({
  quizId,
  course,
  quizzes,
  description,
  onNext,
  onSuccess,
  onFail,
}) => {
  const [quizNo, setQuizNo] = useState(0);
  const [done, setDone] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [proceeding, setProceeding] = useState(false);
  const [result, setResult] = useState({});
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [started, setStarted] = useState(false);

  const lessonContent = useMemo(() => {
    if (description) {
      return replaceVideoScripts(description);
    }

    return '';
  }, [description]);

  useEffect(() => {
    const anws = localStorage.getItem(`acre_quiz_${quizId}`);
    if (anws) {
      const parsedAnswers = JSON.parse(anws);
      setAnswers(parsedAnswers.slice());
    } else {
      setAnswers(quizzes.map(() => []));
    }
  }, [quizId, quizzes]);

  // useEffect(() => {
  //   if (done === 1) {
  //     onSuccess();
  //   } else if (done === -1) {
  //     onFail();
  //   }
  // }, [done]);

  if (description && !started) {
    return (
      <div className="w-full html-content quiz">
        <div
          dangerouslySetInnerHTML={{
            __html: lessonContent,
          }}
        />
        <div className="flex font-semibold text-black sm:text-sm sm:justify-center sm:w-full">
          <div className="flex items-center mr-8">
            <QuestionIcon className="mr-2" />
            <span className="text-light-yellow mr-1">
              {quizzes.length}
            </span>{' '}
            Questions
          </div>
          <div className="flex items-center">
            <ClockIcon className="mr-2" />
            Time <span className="ml-1 text-light-yellow">01:00:00</span>
          </div>
        </div>
        <div className="w-full flex justify-center">
          <Button
            className={`w-48 font-semibold m-auto py-2 px-4 mt-9 sm:my-5 sm:h-9 sm:text-base sm:font-normal border border-solid rounded-lg text-white bg-dark-green`}
            type="misc"
            caption="Start Quiz"
            onClick={() => setStarted(true)}
          />
        </div>
      </div>
    );
  }

  const handleRetake = () => {
    setStarted(false);
    setProceeding(false);
    setResult({});
    setDone(0);
    setQuizNo(0);
    setAnswers(quizzes.map(() => []));
    localStorage.removeItem(`acre_quiz_${quizId}`);
  };
  const handleAnswerChange = (val) => {
    const quiz = quizzes[quizNo];
    const anws = answers.slice();
    anws[quizNo] = quiz.type === 'single' ? [val] : val;
    setAnswers(anws);
    // store quiz status
    localStorage.setItem(`acre_quiz_${quizId}`, JSON.stringify(anws));
  };
  const handleNext = () => {
    setQuizNo(quizNo + 1);
  };
  const handlePrev = () => {
    setQuizNo(quizNo - 1);
  };
  const handleSubmit = async (e) => {
    if (quizNo === quizzes.length - 1) {
      setProceeding(true);
      try {
        const res = await evaluateAnswers({ quizId, answers });
        if (res.data.status === 1) {
          const status = res.data.data;
          setWrongAnswers(status.wrongAnswers);
          if (status.passed) {
            setDone(1);
          } else {
            setDone(-1);
          }
          setResult(status);
          setProceeding(false);
          return;
        }
        throw Error(res.data.message);
      } catch (err) {
        console.log('Error in submitting answers', err);
        notify(
          'error',
          'Error in submitting answers: ' + (err && err.toString())
        );
      }
      setProceeding(false);
    } else {
      setQuizNo(Math.min(quizNo + 1, quizzes.length - 1));
    }
  };

  const renderQuiz = () => {
    const quiz = quizzes[quizNo];
    const ComponentToRender =
      quiz.type === 'single' ? RadioGroup : CheckboxGroup;
    return (
      <>
        <div className="text-xl text-primary text-center px-6 py-4">
          {quiz.question}
        </div>
        <div className="px-5 py-8 sm:p-0">
          <ComponentToRender
            value={
              quiz.type === 'single'
                ? answers[quizNo] && answers[quizNo][0]
                : answers[quizNo]
            }
            options={quiz.options.map((opt, key) => ({ label: opt, key }))}
            onChange={handleAnswerChange}
          />
        </div>
      </>
    );
  };

  if (done !== 0) {
    return (
      <QuizContainer className="flex flex-col">
        <div className="hidden sm:block course-title">{course}</div>
        {done === 1 ? (
          <>
            <div className="text-2xl text-dark-green py-4 font-bold">
              Congratulations!
            </div>
            <div className="py-8">
              You got {quizzes.length - wrongAnswers.length}/{quizzes.length}{' '}
              correct!
              {Array.isArray(wrongAnswers) && wrongAnswers.length > 0 && (
                <>
                  <br />
                  You missed questions:{' '}
                  {wrongAnswers.map((no) => no + 1).join(', ')}
                </>
              )}
            </div>
            <div className="flex items-center justify-between sm:mb-5">
              <Button
                className={`w-48 sm:w-24 sm:h-8 font-semibold py-2 px-4 border border-solid rounded-lg text-white bg-primary`}
                type="misc"
                caption="Retake"
                onClick={handleRetake}
              />
              <Button
                className={`w-48 sm:w-24 sm:h-8 font-semibold py-2 px-4 border border-solid rounded-lg text-white bg-primary`}
                type="misc"
                caption="Mark Complete"
                onClick={onSuccess}
              />
            </div>
          </>
        ) : (
          <>
            <div className="text-2xl text-red py-4 font-bold">
              Sorry, Please Check Your Answers!
            </div>
            <div className="py-8">
              You missed questions:{' '}
              {wrongAnswers.map((no) => no + 1).join(', ')}
              <br />
              You got {quizzes.length - wrongAnswers.length}/{quizzes.length}{' '}
              correct.
            </div>
            <Button
              className={`w-48 sm:w-24 sm:mb-5 sm:h-8 font-semibold py-2 px-4 border border-solid rounded-lg text-white bg-primary`}
              type="misc"
              caption="Retake"
              onClick={handleRetake}
            />
          </>
        )}
      </QuizContainer>
    );
  }

  return (
    <QuizContainer className="flex flex-col">
      <div className="hidden sm:block course-title">{course}</div>
      <div className="mb-9 text-black sm:mb-0">
        <span className="ml-9 mb-2 font-semibold text-primary text-sm">
          Question {quizNo + 1} / {quizzes.length}
        </span>
        <ProgressBar value={(quizNo * 100) / quizzes.length} height={4} />
      </div>
      {renderQuiz()}
      <div className="actions flex items-center mx-4 sm:mb-5 sm:mx-0 sm:mt-5">
        {quizNo > 0 && (
          <Button
            className={`w-48 sm:w-24 sm:h-8 font-semibold py-2 mr-4 px-4 border-2 border-solid border-light-yellow rounded-lg text-light-yellow bg-transparent`}
            type="misc"
            caption={
              <>
                <ArrowLeftIcon className="mr-2" /> Back
              </>
            }
            onClick={handlePrev}
          />
        )}
        {quizNo === quizzes.length - 1 && (
          <Button
            className={`w-48 sm:w-24 sm:h-8 font-semibold py-2 px-4 border border-solid rounded-lg text-white bg-primary`}
            type="misc"
            caption="Submit"
            busy={proceeding}
            onClick={handleSubmit}
          />
        )}
        {quizNo < quizzes.length - 1 && (
          <Button
            className={`w-48 sm:w-25 sm:h-8 font-semibold py-2 px-4 border border-solid rounded-lg text-white bg-light-yellow`}
            type="misc"
            caption={
              <>
                Next
                <ArrowRightIcon className="ml-2" />
              </>
            }
            onClick={handleNext}
          />
        )}
      </div>
    </QuizContainer>
  );
};

export default Quizzes;
